/* RL Press — « Vu dans la presse » slider (rl-section-press.liquid).
   Vanilla, no module. Prefixed globals only.

   The logos are rendered three times (clone | real | clone); `pos` is an
   index in that tripled list and starts on the real copy. After a move
   that leaves the middle copy, the track jumps back to the equivalent
   index with transitions off, so the loop never ends.

   - Desktop and tablet show 3 logos, the active one centred; mobile shows
     1 or 2, the active one first. The visible count is read from the
     layout (--rl-press-visible), so CSS stays the single source.
   - Autoplay every data-interval ms; paused on hover, touch, keyboard
     focus, when the section is off screen or the tab hidden, and never
     started under prefers-reduced-motion.
   - The quote zone announces changes (aria-live polite) only while the
     autoplay is paused, so screen readers are not spammed. */
(function () {
  'use strict';

  var RL_PRESS_SWIPE_MIN = 40;
  var RL_PRESS_SETTLE_MS = 720;

  function RL_PRESS_init(section) {
    if (!section || section.getAttribute('data-rl-press-ready') === 'true') return;
    var track = section.querySelector('[data-rl-press-track]');
    var viewport = section.querySelector('[data-rl-press-viewport]');
    var quotes = section.querySelectorAll('[data-rl-press-quote]');
    var quoteZone = section.querySelector('[data-rl-press-quotes]');
    var n = quotes.length;
    if (!track || !viewport || n < 2) return;
    section.setAttribute('data-rl-press-ready', 'true');

    var items = track.children;
    var logos = track.querySelectorAll('[data-rl-press-logo]');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var autoplay = section.getAttribute('data-autoplay') === 'true';
    var interval = parseInt(section.getAttribute('data-interval'), 10) || 5000;
    var pos = n;
    var timer = null;
    var settle = null;
    var holds = {};
    var inView = true;
    var x0 = null;

    function visibleCount() {
      var first = items[0];
      if (!first || !viewport.clientWidth) return 1;
      return Math.max(1, Math.round(viewport.clientWidth / first.getBoundingClientRect().width));
    }

    function place() {
      var width = items[0] ? items[0].getBoundingClientRect().width : 0;
      // Three in view: the active logo sits in the middle slot.
      var offset = visibleCount() >= 3 ? 1 : 0;
      track.style.transform = 'translateX(' + (-(pos - offset) * width) + 'px)';
    }

    function paint() {
      var active = ((pos % n) + n) % n;
      for (var i = 0; i < items.length; i++) {
        items[i].classList.toggle('is-active', i === pos);
      }
      // aria-current on the real copy only (the clones are aria-hidden).
      for (var j = n; j < 2 * n && j < logos.length; j++) {
        if (j - n === active) logos[j].setAttribute('aria-current', 'true');
        else logos[j].removeAttribute('aria-current');
      }
      for (var k = 0; k < n; k++) {
        quotes[k].classList.toggle('is-active', k === active);
      }
    }

    function setPos(next) {
      pos = next;
      track.classList.remove('is-resetting');
      paint();
      place();
      window.clearTimeout(settle);
      settle = window.setTimeout(function () {
        if (pos >= n && pos < 2 * n) return;
        pos = n + (((pos % n) + n) % n);
        track.classList.add('is-resetting');
        paint();
        place();
        // Force the jump to apply before transitions come back.
        void track.offsetWidth;
        track.classList.remove('is-resetting');
      }, reduced.matches ? 0 : RL_PRESS_SETTLE_MS);
    }

    function go(step) {
      setPos(pos + step);
    }

    function paused() {
      for (var reason in holds) {
        if (Object.prototype.hasOwnProperty.call(holds, reason) && holds[reason]) return true;
      }
      return !inView || document.hidden;
    }

    function sync() {
      var running = autoplay && !reduced.matches && !paused();
      if (quoteZone) quoteZone.setAttribute('aria-live', running ? 'off' : 'polite');
      if (running && !timer) {
        timer = window.setInterval(function () { go(1); }, interval);
      } else if (!running && timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function hold(reason, value) {
      holds[reason] = value;
      sync();
    }

    var prev = section.querySelector('[data-rl-press-prev]');
    var next = section.querySelector('[data-rl-press-next]');
    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    for (var i = 0; i < items.length; i++) {
      (function (index) {
        var button = items[index].querySelector('[data-rl-press-logo]');
        if (button) button.addEventListener('click', function () { setPos(index); });
      })(i);
    }

    section.addEventListener('mouseenter', function () { hold('hover', true); });
    section.addEventListener('mouseleave', function () { hold('hover', false); });
    section.addEventListener('focusin', function () { hold('focus', true); });
    section.addEventListener('focusout', function (event) {
      if (!section.contains(event.relatedTarget)) hold('focus', false);
    });

    viewport.addEventListener('touchstart', function (event) {
      x0 = event.touches[0].clientX;
      hold('touch', true);
    }, { passive: true });
    viewport.addEventListener('touchend', function (event) {
      if (x0 !== null) {
        var dx = event.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > RL_PRESS_SWIPE_MIN) go(dx < 0 ? 1 : -1);
      }
      x0 = null;
      hold('touch', false);
    }, { passive: true });

    document.addEventListener('visibilitychange', sync);
    if (reduced.addEventListener) reduced.addEventListener('change', sync);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        sync();
      }).observe(section);
    }

    // Widths change with the viewport: keep the active logo in place.
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        track.classList.add('is-resetting');
        place();
        void track.offsetWidth;
        track.classList.remove('is-resetting');
      }).observe(viewport);
    } else {
      window.addEventListener('resize', place);
    }

    // Theme editor: selecting a publication block shows it and holds the autoplay.
    section.addEventListener('shopify:block:select', function (event) {
      var index = parseInt(event.target.getAttribute('data-rl-press-quote'), 10);
      if (!isNaN(index)) setPos(n + index);
      hold('editor', true);
    });
    section.addEventListener('shopify:block:deselect', function () { hold('editor', false); });

    track.classList.add('is-resetting');
    paint();
    place();
    void track.offsetWidth;
    track.classList.remove('is-resetting');
    sync();
  }

  function RL_PRESS_initAll(root) {
    var sections = (root || document).querySelectorAll('[data-rl-press]');
    for (var i = 0; i < sections.length; i++) RL_PRESS_init(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { RL_PRESS_initAll(); });
  } else {
    RL_PRESS_initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    RL_PRESS_initAll(event.target);
  });
})();
